import { DEFAULT_YAK_TRAILS_CONFIG, type GameSnapshot, type PlayerSnapshot } from "@games-monorepo/yak-trails-core";
import { MathUtils } from "three";

import { YAK_TRAILS_CLIENT_TUNING } from "../tuning";

export interface PresentationPose {
  x: number;
  y: number;
  headingX: number;
  headingY: number;
}

export interface PresentationMotionState extends PresentationPose {
  initialized: boolean;
  authoritativeX: number;
  authoritativeY: number;
  authoritativeHeadingX: number;
  authoritativeHeadingY: number;
  lastSnapshotTick: number;
  lastSnapshotEpoch: number;
  lastStatus: PlayerSnapshot["status"];
}

export interface PresentationMotionOptions {
  deltaSeconds: number;
  isSelf: boolean;
  latencyMs?: number;
  steering?: { x: number; y: number } | null;
}

function normalizedDirection(direction: { x: number; y: number }, fallback: { x: number; y: number }) {
  const length = Math.hypot(direction.x, direction.y);
  if (length < YAK_TRAILS_CLIENT_TUNING.motion.directionEpsilon) return fallback;
  return { x: direction.x / length, y: direction.y / length };
}

function rotateToward(
  current: { x: number; y: number },
  target: { x: number; y: number },
  maximumRadians: number,
) {
  const currentAngle = Math.atan2(current.y, current.x);
  const targetAngle = Math.atan2(target.y, target.x);
  const difference = MathUtils.euclideanModulo(targetAngle - currentAngle + Math.PI, Math.PI * 2) - Math.PI;
  const angle = currentAngle + MathUtils.clamp(difference, -maximumRadians, maximumRadians);
  return { x: Math.cos(angle), y: Math.sin(angle) };
}

function advancePose(
  pose: PresentationPose,
  desiredHeading: { x: number; y: number },
  deltaSeconds: number,
): PresentationPose {
  const config = DEFAULT_YAK_TRAILS_CONFIG;
  const heading = rotateToward(
    { x: pose.headingX, y: pose.headingY },
    normalizedDirection(desiredHeading, { x: pose.headingX, y: pose.headingY }),
    config.mechanics.maximumTurnRadiansPerSecond * deltaSeconds,
  );
  let x = pose.x + heading.x * config.mechanics.movementSpeed * deltaSeconds;
  let y = pose.y + heading.y * config.mechanics.movementSpeed * deltaSeconds;
  const boundary = config.world.radius - config.mechanics.boundaryInset;
  const radius = Math.hypot(x, y);
  if (radius > boundary) {
    const radialX = x / radius;
    const radialY = y / radius;
    const left = { x: -radialY, y: radialX };
    const right = { x: radialY, y: -radialX };
    const leftDot = heading.x * left.x + heading.y * left.y;
    const rightDot = heading.x * right.x + heading.y * right.y;
    const tangent = leftDot >= rightDot ? left : right;
    x = radialX * boundary;
    y = radialY * boundary;
    heading.x = tangent.x;
    heading.y = tangent.y;
  }
  return { x, y, headingX: heading.x, headingY: heading.y };
}

function advanceByDuration(
  pose: PresentationPose,
  desiredHeading: { x: number; y: number },
  durationSeconds: number,
): PresentationPose {
  let result = pose;
  let remaining = Math.max(0, durationSeconds);
  while (remaining > 0) {
    const step = Math.min(YAK_TRAILS_CLIENT_TUNING.motion.maximumFrameSeconds, remaining);
    result = advancePose(result, desiredHeading, step);
    remaining -= step;
  }
  return result;
}

export function createPresentationMotionState(player: PlayerSnapshot): PresentationMotionState {
  return {
    x: player.x,
    y: player.y,
    headingX: player.headingX,
    headingY: player.headingY,
    initialized: false,
    authoritativeX: player.x,
    authoritativeY: player.y,
    authoritativeHeadingX: player.headingX,
    authoritativeHeadingY: player.headingY,
    lastSnapshotTick: -1,
    lastSnapshotEpoch: -1,
    lastStatus: player.status,
  };
}

export function advancePresentationMotion(
  state: PresentationMotionState,
  player: PlayerSnapshot,
  snapshot: Pick<GameSnapshot, "tick" | "epoch">,
  options: PresentationMotionOptions,
): PresentationMotionState {
  const deltaSeconds = MathUtils.clamp(options.deltaSeconds, 0, YAK_TRAILS_CLIENT_TUNING.motion.maximumFrameSeconds);
  const desiredHeading = options.isSelf && options.steering
    ? normalizedDirection(options.steering, { x: player.headingX, y: player.headingY })
    : { x: player.headingX, y: player.headingY };
  const isNewSnapshot = snapshot.tick !== state.lastSnapshotTick || snapshot.epoch !== state.lastSnapshotEpoch;
  const statusChanged = player.status !== state.lastStatus;

  if (!state.initialized || statusChanged || player.status !== "active") {
    state.x = player.x;
    state.y = player.y;
    state.headingX = player.headingX;
    state.headingY = player.headingY;
    state.authoritativeX = player.x;
    state.authoritativeY = player.y;
    state.authoritativeHeadingX = player.headingX;
    state.authoritativeHeadingY = player.headingY;
    state.initialized = player.status === "active";
  }

  if (isNewSnapshot && player.status === "active") {
    const latencyLeadSeconds = MathUtils.clamp(
      (options.latencyMs ?? 0) / 2_000,
      0,
      YAK_TRAILS_CLIENT_TUNING.motion.maximumLatencyLeadSeconds,
    );
    const projected = advanceByDuration({
      x: player.x,
      y: player.y,
      headingX: player.headingX,
      headingY: player.headingY,
    }, desiredHeading, latencyLeadSeconds);
    state.authoritativeX = projected.x;
    state.authoritativeY = projected.y;
    state.authoritativeHeadingX = projected.headingX;
    state.authoritativeHeadingY = projected.headingY;

    const correctionDistance = Math.hypot(state.authoritativeX - state.x, state.authoritativeY - state.y);
    if (
      !state.initialized
      || snapshot.epoch !== state.lastSnapshotEpoch
      || correctionDistance > YAK_TRAILS_CLIENT_TUNING.motion.hardReconciliationDistance
    ) {
      state.x = state.authoritativeX;
      state.y = state.authoritativeY;
      state.headingX = state.authoritativeHeadingX;
      state.headingY = state.authoritativeHeadingY;
      state.initialized = true;
    }
  }

  state.lastSnapshotTick = snapshot.tick;
  state.lastSnapshotEpoch = snapshot.epoch;
  state.lastStatus = player.status;
  if (player.status !== "active" || deltaSeconds <= 0) return state;

  const authoritative = advancePose({
    x: state.authoritativeX,
    y: state.authoritativeY,
    headingX: state.authoritativeHeadingX,
    headingY: state.authoritativeHeadingY,
  }, desiredHeading, deltaSeconds);
  state.authoritativeX = authoritative.x;
  state.authoritativeY = authoritative.y;
  state.authoritativeHeadingX = authoritative.headingX;
  state.authoritativeHeadingY = authoritative.headingY;

  const presented = advancePose(state, desiredHeading, deltaSeconds);
  state.x = presented.x;
  state.y = presented.y;
  state.headingX = presented.headingX;
  state.headingY = presented.headingY;

  const errorX = state.authoritativeX - state.x;
  const errorY = state.authoritativeY - state.y;
  const errorDistance = Math.hypot(errorX, errorY);
  if (errorDistance > YAK_TRAILS_CLIENT_TUNING.motion.hardReconciliationDistance) {
    state.x = state.authoritativeX;
    state.y = state.authoritativeY;
  } else if (errorDistance > YAK_TRAILS_CLIENT_TUNING.motion.correctionDeadZone) {
    const reconciliationRate = options.isSelf
      ? YAK_TRAILS_CLIENT_TUNING.motion.selfReconciliationRate
      : YAK_TRAILS_CLIENT_TUNING.motion.remoteReconciliationRate;
    const blend = 1 - Math.exp(-reconciliationRate * deltaSeconds);
    state.x += errorX * blend;
    state.y += errorY * blend;
  }
  return state;
}

export function projectedPlayerPosition(
  player: PlayerSnapshot,
  snapshot: Pick<GameSnapshot, "serverTimeMs">,
  isSelf: boolean,
  nowMs = Date.now(),
): { x: number; y: number } {
  const snapshotAgeSeconds = Math.max(0, (nowMs - snapshot.serverTimeMs) / 1_000);
  const presentationLeadSeconds = isSelf
    ? YAK_TRAILS_CLIENT_TUNING.motion.selfPresentationLeadSeconds
    : YAK_TRAILS_CLIENT_TUNING.motion.remotePresentationLeadSeconds;
  const predictionSeconds = MathUtils.clamp(
    snapshotAgeSeconds + presentationLeadSeconds,
    0,
    YAK_TRAILS_CLIENT_TUNING.motion.maximumExtrapolationSeconds,
  );
  const distance = DEFAULT_YAK_TRAILS_CONFIG.mechanics.movementSpeed * predictionSeconds;
  return {
    x: player.x + player.headingX * distance,
    y: player.y + player.headingY * distance,
  };
}

export function cameraZoomForTerritory(territoryPercent: number): number {
  const camera = YAK_TRAILS_CLIENT_TUNING.camera;
  return MathUtils.clamp(
    camera.maximumZoom - territoryPercent * camera.zoomPerTerritoryPercent,
    camera.minimumZoom,
    camera.maximumZoom,
  );
}

export function dampAngle(current: number, target: number, smoothing: number, deltaSeconds: number): number {
  const difference = MathUtils.euclideanModulo(target - current + Math.PI, Math.PI * 2) - Math.PI;
  return current + difference * (1 - Math.exp(-smoothing * deltaSeconds));
}
